/**
 * MegatronScanner hook: spreadsheet-like slot navigation + search.
 *
 * Two modes:
 * - Select mode: ↑/↓ navigate slots, Backspace clears, Enter opens search
 * - Search mode: type to search, ↑/↓ navigate results, Enter picks, Esc exits
 *
 * Barcode scanning works in both modes (rapid digit sequence detection).
 *
 * Uses the same INPUT_TAGS guard as KeyboardShortcuts.js to reliably detect
 * when the user is typing in a form field.
 */

const INPUT_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

export const MegatronScanner = {
  mounted() {
    this.buffer = "";
    this.lastKeyTime = 0;
    this.highlightIndex = -1;
    this.INTER_KEY_THRESHOLD = 100;
    this.MIN_BARCODE_LENGTH = 8;
    this.MAX_BARCODE_LENGTH = 18;

    this.handleGlobalKeyDown = (e) => {
      const active = document.activeElement;
      const inInput = INPUT_TAGS.has(active?.tagName) || active?.isContentEditable;
      const searchInput = document.getElementById("megatron-search-input");
      const inSearch = active === searchInput;

      // -- Barcode detection (works in any mode) --
      const now = Date.now();
      const timeSinceLastKey = now - this.lastKeyTime;
      const isTerminator = e.key === "Tab" || e.key === "Enter";

      if (isTerminator && this.buffer.length >= this.MIN_BARCODE_LENGTH) {
        e.preventDefault();
        e.stopPropagation();
        const barcode = this.buffer;
        this.buffer = "";
        this.highlightIndex = -1;
        this.pushEvent("barcode_scan", { barcode });
        if (searchInput) searchInput.value = "";
        return;
      }

      if (/^\d$/.test(e.key)) {
        if (timeSinceLastKey > this.INTER_KEY_THRESHOLD) {
          this.buffer = "";
        }
        this.buffer += e.key;
        this.lastKeyTime = now;
        if (this.buffer.length > this.MAX_BARCODE_LENGTH) {
          this.buffer = this.buffer.slice(-this.MAX_BARCODE_LENGTH);
        }
        if (inInput) return;
      }

      if (!isTerminator && !/^\d$/.test(e.key)) {
        this.buffer = "";
      }

      // -- Search mode (search input focused) --
      if (inSearch) {
        const results = this.el.querySelectorAll("[data-result-id]");

        if (e.key === "Escape") {
          e.preventDefault();
          this.highlightIndex = -1;
          searchInput.value = "";
          searchInput.blur();
          this.pushEvent("exit_search", {});
          return;
        }

        if (e.key === "Enter" && results.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          const idx = Math.max(0, this.highlightIndex);
          const target = results[idx];
          if (target) target.click();
          this.highlightIndex = -1;
          return;
        }

        if (e.key === "ArrowDown" && results.length > 0) {
          e.preventDefault();
          this.highlightIndex = Math.min(this.highlightIndex + 1, results.length - 1);
          this._updateHighlight(results);
          return;
        }

        if (e.key === "ArrowUp" && results.length > 0) {
          e.preventDefault();
          this.highlightIndex = Math.max(this.highlightIndex - 1, 0);
          this._updateHighlight(results);
          return;
        }

        // Let all other keys pass through to the input naturally
        return;
      }

      // If focused on any other input (e.g. the select dropdown), don't
      // intercept keys except Escape
      if (inInput) {
        if (e.key === "Escape") {
          active.blur();
        }
        return;
      }

      // -- Select mode (no input focused) --
      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        this.pushEvent("navigate_slot", { direction: "down" });
        return;
      }

      if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        this.pushEvent("navigate_slot", { direction: "up" });
        return;
      }

      if (e.key === "Home") {
        e.preventDefault();
        this.pushEvent("navigate_slot", { direction: "home" });
        return;
      }

      if (e.key === "End") {
        e.preventDefault();
        this.pushEvent("navigate_slot", { direction: "end" });
        return;
      }

      if (e.key === " " && e.shiftKey) {
        e.preventDefault();
        this.pushEvent("navigate_slot", { direction: "next_empty" });
        return;
      }

      if (e.key === " ") {
        e.preventDefault();
        this.pushEvent("navigate_slot", { direction: "past_last" });
        return;
      }

      if ((e.key === "Backspace" || e.key === "Delete") && e.shiftKey) {
        e.preventDefault();
        this.pushEvent("delete_and_shift_slot", {});
        return;
      }

      if (e.key === "Backspace" || e.key === "Delete") {
        e.preventDefault();
        this.pushEvent("clear_slot", {});
        return;
      }

      if (e.key === "Enter" && e.shiftKey) {
        e.preventDefault();
        this.pushEvent("insert_blank_slot", {});
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        this.pushEvent("enter_search", {});
        return;
      }
    };

    window.addEventListener("keydown", this.handleGlobalKeyDown);

    this.handleEvent("focus_search", () => {
      requestAnimationFrame(() => {
        const input = document.getElementById("megatron-search-input");
        if (input) {
          input.value = "";
          input.focus();
        }
      });
    });

    this.handleEvent("exit_search", () => {
      requestAnimationFrame(() => {
        const input = document.getElementById("megatron-search-input");
        if (input) {
          input.value = "";
          input.blur();
        }
      });
    });

    this.handleEvent("scroll_to_slot", ({ slot }) => {
      requestAnimationFrame(() => {
        const slotEl = document.querySelector(`[data-slot-number="${slot}"]`);
        if (slotEl) {
          slotEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    });
  },

  _updateHighlight(results) {
    results.forEach((el, i) => {
      if (i === this.highlightIndex) {
        el.classList.add("bg-cyan-500/20", "text-cyan-300");
        el.classList.remove("hover:bg-zinc-700");
        el.scrollIntoView({ block: "nearest" });
      } else {
        el.classList.remove("bg-cyan-500/20", "text-cyan-300");
        el.classList.add("hover:bg-zinc-700");
      }
    });
  },

  destroyed() {
    window.removeEventListener("keydown", this.handleGlobalKeyDown);
  },
};
